import { useBreakpoint, useTheme, useToggle } from '../../hooks';
import React from 'react';
import { Post } from '../../types/Post';
import { Stack } from '@mui/material';
import { BodyLarge, Row, TitleMedium } from '../../styles';
import { useTranslations } from 'next-intl';
import { TextButton } from '../buttons';
import { Edit } from '@mui/icons-material';
import { ProjectDetailsCoWorkPost } from './post/ProjectDetailsCoWorkPost';
import { ProjectPostCreationModal } from './create-edit-post/modal/ProjectDetailsCoWorkCreatePostModal';
import { CardContainer } from '../project-card/styles';

interface BaseProps {
    posts: Post[];
    refreshPosts: () => void;
    canCreatePost: boolean;
    canDeletePosts: boolean;
}

interface ProjectProps {
    projectId: string;
}

type Props = BaseProps & Partial<ProjectProps>;

export const PostSection = ({
    posts,
    refreshPosts,
    canCreatePost,
    projectId,
    canDeletePosts
}: Props) => {
    const t = useTranslations('postSection');
    const viewSize = useBreakpoint();
    const { colors } = useTheme();

    const [showPostCreation, togglePostCreation] = useToggle(false);

    function renderEmptyCard() {
        return (
            <CardContainer breakpoint={viewSize}>
                <Stack flexDirection={'column'} alignItems={'center'} gap={1} py={3}>
                    <TitleMedium>{t('post_section_no_posts_title')}</TitleMedium>
                    <BodyLarge
                        whiteSpace={'pre-line'}
                        textAlign={'center'}
                        color={colors.onSurfaceVariant}
                    >
                        {t('post_section_no_posts_description')}
                    </BodyLarge>
                </Stack>
            </CardContainer>
        );
    }

    function renderPosts() {
        if (posts.length === 0) {
            return renderEmptyCard();
        } else {
            return posts.map((p) => (
                <ProjectDetailsCoWorkPost
                    key={p.id}
                    post={p}
                    refresh={refreshPosts}
                    canDeletePosts={canDeletePosts}
                    canCreatePosts={canCreatePost}
                />
            ));
        }
    }

    return (
        <>
            <Stack gap={1}>
                <Row stretch px={1}>
                    <TitleMedium color={colors.onSurfaceVariant}>
                        {t('post_section_headline')}
                    </TitleMedium>
                    {canCreatePost && (
                        <TextButton
                            icon={<Edit />}
                            onClick={() => togglePostCreation()}
                            label={t('post_section_write_post_btn_label')}
                        />
                    )}
                </Row>
                {renderPosts()}
            </Stack>

            {projectId !== undefined && showPostCreation && (
                <ProjectPostCreationModal
                    projectId={projectId}
                    onClose={() => togglePostCreation()}
                    onSuccess={refreshPosts}
                />
            )}
        </>
    );
};
